import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { HeadingComponent } from '../../../../shared/components/heading/heading.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';

import { JobBoardService } from '../../../../shared/services/job-board.service';
import { UserService } from '../../../../shared/services/user.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserClientVm } from '../../../../store/user/user.interface';
import { environment } from '../../../../../environments/environment';
import { constants } from '../../../../shared/constants/constants';
import { StorageService } from '../../../../shared/services/storage.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { InputTextModule } from 'primeng/inputtext';

interface SortOption {
  name: string;
  field: string;
  defaultDirection: 'asc' | 'desc';
}

@Component({
    selector: 'app-active-job',
    standalone: true,
    templateUrl: './active.component.html',
    styleUrl: './active.component.scss',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        HeadingComponent,
        RadioButtonModule,
        DropdownModule,
        FloatLabelModule,
        CheckboxModule,
        DialogModule,
        IconFieldModule,
        InputIconModule,
        PipesModule,
        InputTextModule
    ],
    providers: [DialogService]
})
export class ActiveComponent implements OnInit {
  jobs: any[] = [];
  jobsCount: number = 0;
  expandedRows: { [key: string]: boolean } = {};
  user!: UserClientVm;
  appName = environment.title;
  ref: DynamicDialogRef | null = null;
  selectedTenant: any = null;

  // --- Traditional properties for UI state ---
  searchText: string = '';
  searchTextValue = '';
  searchFieldOptions = [
    { name: 'All', value: 'all', defaultDirection: 'desc' },
    { name: 'Name', value: 'title', defaultDirection: 'asc' },
    { name: 'Start Date', value: 'startDate', defaultDirection: 'desc' },
    { name: 'Publish Date', value: 'publishedAt', defaultDirection: 'desc' },
    { name: 'Rate', value: 'rate', defaultDirection: 'desc' },
    { name: 'Description', value: 'description' , defaultDirection: 'desc' }
  ];
  selectedSearchField = this.searchFieldOptions[0];
  selectedSearchFieldValue = this.searchFieldOptions[0];
  notSpecified: boolean = false;
  notSpecifiedValue = false;
  byExperience: boolean = false;
  byExperienceValue = false;

  sortOptions: SortOption[] = [
    { name: 'All', field: 'all', defaultDirection: 'desc' },
    { name: 'Name', field: 'title', defaultDirection: 'asc' },
    { name: 'Start Date', field: 'startDate', defaultDirection: 'desc' },
    { name: 'Publish Date', field: 'publishedAt', defaultDirection: 'desc' },
    { name: 'Rate', field: 'rate', defaultDirection: 'desc' },
    { name: 'Description', field: 'description' , defaultDirection: 'desc' }
  ];
  selectedSortOption = this.sortOptions[0];
  selectedSortOptionValue = this.sortOptions[0];
  sortDirection: 'asc' | 'desc' = this.sortOptions[0].defaultDirection;

  get sortValue(): string {
    const direction = this.sortDirection;
    return direction === 'desc' ? `-1` : '1';
  }

  get sortDisplayText(): string {
    const direction = this.sortDirection;
    const directionText = direction === 'asc' ? 'Ascending' : 'Descending';
    const filterValue = this.selectedSearchField.name;
    return `${filterValue} (${directionText})`;
  }

  // --- Dialogs and modals ---
  modalContent = '';
  visible = false;
  modalHeader = '';
  detailModalVisible = false;
  detailModalHeader = '';
  detailModalContent = '';
  applyModalVisible = false;
  applyModalHeader = '';
  applyModalContent = '';
  pendingApplyJob: any = null;
  offset: number = 0;
  rows: number = 10;
  screeningIndex: number = 0;
  screeningAnswers: boolean[] = [];
  screeningModalVisible: boolean = false;
  screeningQuestion: string = '';
  screeningQuestions: any[] = [];

  constructor(
    private jobBoardService: JobBoardService,
    private userService: UserService,
    private dialogService: DialogService,
    private toast: ToastService,
    private storageService: StorageService,
    public sanitizer: DomSanitizer
  ) {}

  async ngOnInit() {
    this.user = this.userService.getSessionUser();
    this.selectedTenant = this.storageService.getTenant();
    await this.fetchActiveJobs();
  }

  async fetchActiveJobs(event?: any) {
    const filters = this.buildBaseFilters(event);
    this.applySearchFilters(filters);
    
    const jobs = await this.jobBoardService.getJobBoardListings(filters);
    this.updateJobsData(jobs);
  }

  private buildBaseFilters(event?: any): any {
    return {
      public: true,
      status: constants.jobBoardTypes.PUBLISHED,
      firmId: this.selectedTenant.firm._id,
      offset: event?.first ?? this.offset,
      limit: event?.rows ?? this.rows,
      sort: this.sortValue,
      sortBy: this.selectedSearchField.value
    };
  }

  private applySearchFilters(filters: any): void {
    const currentField = this.selectedSearchField;
    
    switch (currentField.value) {
      case 'all':
        this.applyAllFieldFilter(filters);
        break;
      case 'title':
        this.applyTitleFieldFilter(filters);
        break;
      case 'rate':
        this.applyRateFieldFilter(filters);
        break;
      case 'description':
        this.applyDescriptionFieldFilter(filters);
        break;
    }
  }

  private applyAllFieldFilter(filters: any): void {
    const trimmedText = this.getTrimmedSearchText();
    if (trimmedText) {
      filters.search = trimmedText;
      filters.searchText = trimmedText;
    }
  }

  private applyTitleFieldFilter(filters: any): void {
    const trimmedText = this.getTrimmedSearchText();
    if (trimmedText) {
      filters.title = trimmedText;
      filters.searchText = trimmedText;
    }
  }

  private applyRateFieldFilter(filters: any): void {
    if (this.notSpecified) {
      this.setNotSpecifiedRateFilters(filters);
    } else if (this.byExperience) {
      this.setByExperienceRateFilters(filters);
    } else {
      this.setSearchTextRateFilters(filters);
    }
  }

  private applyDescriptionFieldFilter(filters: any): void {
    const trimmedText = this.getTrimmedSearchText();
    if (trimmedText) {
      filters.description = trimmedText;
      filters.searchText = trimmedText;
    }
  }

  private getTrimmedSearchText(): string {
    return this.searchText && this.searchText.trim() ? this.searchText.trim() : '';
  }

  private setNotSpecifiedRateFilters(filters: any): void {
    filters.notSpecified = true;
    filters.byExperience = false;
    filters.rate = undefined;
  }

  private setByExperienceRateFilters(filters: any): void {
    filters.byExperience = true;
    filters.notSpecified = false;
    filters.rate = undefined;
  }

  private setSearchTextRateFilters(filters: any): void {
    const trimmedText = this.getTrimmedSearchText();
    if (trimmedText) {
      filters.rate = trimmedText;
      filters.searchText = trimmedText;
      filters.notSpecified = false;
      filters.byExperience = false;
    }
  }

  private updateJobsData(jobs: any[]): void {
    if (jobs && jobs.length) {
      this.jobs = jobs.slice(0, -1);
      this.jobsCount = jobs[jobs.length - 1]?.totalCount ?? 0;
    } else {
      this.jobs = [];
      this.jobsCount = 0;
    }
  }

  onSearch() {
    this.searchText = this.searchTextValue;
    this.fetchActiveJobs();
  }

  onSortOptionChange(event: any) {
    this.selectedSortOption = event.value;
    this.selectedSortOptionValue = event.value;
    this.sortDirection = event.value.defaultDirection;
    this.fetchActiveJobs();
  }

  toggleSortDirection() {
    const currentDirection = this.sortDirection;
    this.sortDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    this.fetchActiveJobs();
  }

  onSearchFieldChange(event: any) {
    this.selectedSearchField = event.value;
    this.selectedSearchFieldValue = event.value;
    this.sortDirection = event.value.defaultDirection;
    this.searchTextValue = '';
    this.searchText = '';
    this.notSpecified = false;
    this.notSpecifiedValue = false;
    this.byExperience = false;
    this.byExperienceValue = false;
    this.fetchActiveJobs();
  }

  onRateFilterChange() {
    this.notSpecified = this.notSpecifiedValue;
    this.byExperience = this.byExperienceValue;
    this.fetchActiveJobs();
  }

  onPageChange(event: any) {
    this.offset = event.first;
    this.rows = event.rows;
    this.fetchActiveJobs(event);
  }

  getLocation(job: any): string {
    if (job.isRemote) return 'Remote';
    if (job.city && job.state && job.postalCode) return `${job.city}, ${job.state}, ${job.postalCode}`;
    return '';
  }

  openDetailModal(job: any, type: 'description' | 'requirement' | 'responsibility') {
    switch(type){
      case 'description':
        this.detailModalContent = job[type] || 'No description available.';
        this.detailModalHeader = 'Description';
        break;
      case 'requirement':
        this.detailModalContent = job[type] || 'No requirements available.';
        this.detailModalHeader = 'Requirements';
        break;
      case 'responsibility':
        this.detailModalContent = job[type] || 'No responsibilities available.';
        this.detailModalHeader = 'Responsibilities';
        break;
    }
    this.detailModalVisible = true;
  }

  apply(job: any) {
    this.pendingApplyJob = job;
    this.applyModalHeader = `Apply for ${job.title}`;
    this.applyModalVisible = true;
  }

  async onApplyConfirm() {
    if (!this.pendingApplyJob) {
      return;
    }

    const conflictQuestions = Array.isArray(this.pendingApplyJob.conflictQuestions)
      ? this.pendingApplyJob.conflictQuestions
      : [];

    if (conflictQuestions.length > 0) {
      this.screeningQuestions = conflictQuestions;
      this.screeningIndex = 0;
      this.screeningAnswers = [];
      this.applyModalVisible = false;
      this.showScreeningQuestion();
      return;
    }

    await this.submitJobApplication();
  }

  showScreeningQuestion() {
    if (
      this.screeningQuestions &&
      this.screeningIndex < this.screeningQuestions.length
    ) {
      this.screeningQuestion = this.screeningQuestions[this.screeningIndex].title;
      this.screeningModalVisible = true;
    } else {
      this.submitJobApplication();
      this.screeningModalVisible = false;
    }
  }

  onScreeningAnswer(answer: boolean) {
    const currentQuestion = this.screeningQuestions[this.screeningIndex];
    if (answer !== currentQuestion.answer) {
      this.screeningModalVisible = false;
      this.applyModalVisible = false;
      this.pendingApplyJob = null;
      this.toast.showInfo(
        `Unfortunately, your job application is rejected.`
      );
      return;
    }
    this.screeningAnswers[this.screeningIndex] = answer;
    this.screeningIndex++;
    this.screeningModalVisible = false;
    setTimeout(() => this.showScreeningQuestion(), 200);
  }

  async submitJobApplication() {
    try {
      await this.jobBoardService.submitJobApplication({
        appliedJobId: this.pendingApplyJob._id,
        appliedCandidateId: this.user._id,
        jobAppliedDate: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
      });
      this.toast.showSuccess('Applied to job successfully.');
      await this.fetchActiveJobs();
    } catch {
      this.toast.showError('Failed to apply.');
    }
    this.applyModalVisible = false;
    this.pendingApplyJob = null;
  }

  onCancelApply() {
    this.applyModalVisible = false;
    this.pendingApplyJob = null;
  }

  onExpandedRowKeysChange(event: any) {
    this.expandedRows = event;
  }

  searchFieldDisabled(){
    return this.selectedSearchField.value === 'rate' && (this.notSpecified || this.byExperience);
  }
}
